import { VENDORS } from 'components/common/vendorConfig';
import type { Bundle } from 'fhir/r4';
import { useRef, useState } from 'react';
import React from 'react';
import { Loader, Message, Grid, Header, Icon, Button } from 'semantic-ui-react';
import useSWR from 'swr';
import type {
  Fetched,
  FlatPatient,
  FoodCatalogBundle,
  FoodItem,
  ProgramBudget,
  ReferralSummary,
} from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FoodFetcher from '../../../../data/FoodFetcher';
import { apiClient } from '../../../../services/api-client';
import * as Helpers from '../../../../utils/Helpers';


import FoodBundleCard from './bundle/foodBundleCard';
import FoodBundleItems from './item/foodBundleItems';
import type { MenuItemClickHandler } from './item/foodBundleItems';
import FoodItemDetail from './item/foodItemDetail';


/** Which bundles are ticked for a multi-order, keyed by `bundleId`. */
type CheckedBundleMap = Record<string, boolean | undefined>;

/** Every fetched bundle, keyed by `bundleId`, for the multi-order lookup. */
type BundlesById = Record<string, FoodCatalogBundle | undefined>;

/**
 * What the catalog screen accepts. Single call site, `../index.tsx`.
 *
 * Almost every field is a pass-through: this component fetches the vendor's
 * bundles, renders a `FoodBundleCard` per bundle, and forwards the rest to
 * `./item/foodBundleItems`. `handleMenuItemClick`, `patient`, `ordersMutate` and
 * `setBudgetOverrideChecked` are dead from here down — see `FoodBundleItemsProps`.
 *
 * Every optional below carries an explicit `| undefined`: under
 * `exactOptionalPropertyTypes` a pass-through parent assigning its own optional prop
 * with no conditional needs the property to admit a present-but-undefined value.
 */
type CatalogProps = {
  /** Forwarded to `FoodBundleItems`. */
  allReferralData?: ReferralSummary[] | undefined;
  /** Forwarded to `FoodBundleItems`, which does not read it. */
  budgetOverrideChecked?: boolean | undefined;
  /** Forwarded to `FoodBundleItems`, which does not read it. */
  handleMenuItemClick?: MenuItemClickHandler | undefined;
  /** Forwarded to `FoodBundleItems`. */
  ordersMealEquivalents?: number | undefined;
  /** Forwarded to `FoodBundleItems`, which does not read it. */
  ordersMutate?: (() => void) | undefined;
  /**
   * The payer org.
   *
   * `| null` because `components/home/index.tsx` mounts the food module with
   * `org={null}` for the roster-wide view and `components/common/food/index.tsx`
   * forwards it here unchanged.
   */
  org?: string | null | undefined;
  /** Forwarded to `FoodBundleItems`, which does not read it. */
  patient?: FlatPatient | undefined;
  /** Keys the per-member bundle selection and gates the multiselect checkboxes. */
  patientId?: string | null | undefined;
  /** Forwarded to `FoodBundleItems`. */
  profileData?: Bundle | Record<string, never> | undefined;
  programBudget?: ProgramBudget[] | undefined;
  programMAXBudget?: number | undefined;
  /** Forwarded to both children; `FoodBundleCard` does not read it. */
  programMAXMealEquivalents?: number | undefined;
  programOrdersCost?: number | undefined;
  setBudgetOverrideChecked?: ((checked: boolean) => void) | undefined;
  /**
   * The four below are required, not optional: `FoodBundleItems` invokes each of
   * them unguarded, and the sole call site (`../index.tsx`) always passes them —
   * each is a `useState` setter, so none can be absent.
   */
  setOrdersMealEquivalents: (mealEquivalents: number) => void;
  setProgramBudget?: ((budget: ProgramBudget[]) => void) | undefined;
  setProgramMAXBudget: (budget: number) => void;
  setProgramOrdersCost: (cost: number) => void;
  /**
   * A vendor code; selects which bundles are fetched.
   *
   * Required: `components/common/food/index.tsx` renders the catalogue only inside
   * `{selectedVendor && selectedVendor.length > 0 && …}`.
   */
  vendor: string;
};

function Catalog(props: CatalogProps) {
  const authInfo = useAuthContext();

  const [loading, setLoading] = React.useState(true);
  const [selectedBundle, setSelectedBundle] = useState<FoodCatalogBundle | null>(null);
  const [clickedItem, setClickedItem] = useState<FoodItem | null>(null);
  const [checkedBundle, setCheckedBundle] = useState<CheckedBundleMap>({});
  const [allBundles, setAllBundles] = useState<BundlesById>({});
  const foodBundleItemsRef = useRef<React.ElementRef<typeof FoodBundleItems>>();

  const bundleOptions = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: Fetched<FoodCatalogBundle[]>, _key: string) => {
      if (data && data.length > 0) {
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. `effectivePeriod` is optional on
        // `FoodOrderBundle` and read unguarded here, so a bundle row without one
        // throws inside the sort comparator. See the defect note on that type;
        // `food/orders/index.tsx` guards the same field. The `!` records the
        // assumption rather than silencing it — a `?.` here would sort those rows
        // to an arbitrary position instead of failing.
        data.sort((a, b) => {
          const bDate: string = new Date(b.effectivePeriod!.end!).toDateString();
          const aDate: string = new Date(a.effectivePeriod!.end!).toDateString();
          return Date.parse(bDate) - Date.parse(aDate);
        });
        const bundle: CheckedBundleMap = {};
        const tempBundles: BundlesById = {};

        data.forEach((element) => {
          bundle[String(element.bundleId)] = false;
          tempBundles[String(element.bundleId)] = element;
        });

        setAllBundles(tempBundles);
        setCheckedBundle(bundle);
        setSelectedBundle(data[0] ?? null);
      }
      setClickedItem(null);
      setLoading(false);
    },
  };

  const {
    data: bundlesData,
    error: bundlesError,
    isValidating: isBundlesLoading,
  } = useSWR<Fetched<FoodCatalogBundle[]>, Error>(
    [`/food/bundle?vendorId=${props.vendor}`, authInfo.token],
    FoodFetcher,
    bundleOptions,
  );

  const handleBundleSelect = (selectedBundle: FoodCatalogBundle) => {
    setSelectedBundle(selectedBundle);
  };

  const handleItemClick = (clickedItem?: FoodItem) => {
    setClickedItem(clickedItem ?? null);
  };

  /** Strips a bundle id's vendor prefix for display; ids without one pass through. */
  const bundleName = (selectedBundle?: string) => {
    if (selectedBundle && selectedBundle.indexOf('-') !== -1) {
      const index = selectedBundle.indexOf('-') + 1;
      return selectedBundle.substr(index);
    } else {
      return selectedBundle;
    }
  };

  /**
   * The bundle items for every ticked bundle, fetched on demand for a multi-order.
   *
   * `FoodItem[]` is an unchecked claim at this boundary, as elsewhere in the food
   * module: the response is unvalidated JSON and `apiClient.get` defaults to
   * `unknown` when no type argument is named.
   */
  const FHIRFetcherOnDemand = async (endPoint: string, token: string) => {
    const dataEndPoint = endPoint;

    const data = await apiClient.get<FoodItem[]>(
      import.meta.env.VITE_FOODCOURT_API_NAME,
      dataEndPoint,
      {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      },
    );
    return data;
  };

  const prepareMultiOrder = async () => {
    const selectedBundles: string[] = [];

    for (const [key, value] of Object.entries(checkedBundle)) {
      if (value) {
        selectedBundles.push(key);
      }
    }

    const urls: string = `/food/bundle-item?bundleId:in=${selectedBundles.join()}`;

    const selectedBundleItems = await FHIRFetcherOnDemand(urls, authInfo.token);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    foodBundleItemsRef.current!.prepareMultiOrder(selectedBundleItems);
  };

  const handleCreateMultiOrder = () => {
    if (authInfo.validateUSPSAddress) {
      setLoading(true);
      Helpers.validatePatientAddress(props.patientId, props.org, authInfo.token).then(
        (_success) => {
          setLoading(false);
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
          prepareMultiOrder();
        },
        (_errors) => {
          setLoading(false);
          // setConfirmType('FAILURE_ADDRESS_VALIDATION');
          // setConfirmOpen(true);
        },
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      prepareMultiOrder();
    }
  };

  return (
    <>
      {isBundlesLoading ? (
        <Loader active inline="centered" content="Loading bundles..." />
      ) : (
        <div>
          {loading && !bundlesData && !bundlesError && <Loader active inline="centered" />}
          {!bundlesError && bundlesData && bundlesData.length === 0 && (
            <div style={{ height: '400px' }}>
              <Message warning header="Info" content="No bundles found for this vendor." />
            </div>
          )}
          {!bundlesError && bundlesData && bundlesData.length > 0 && (
            <Grid cols={3} padded={'horizontally'} stretched>
              <Grid.Row divided>
                <Grid.Column width="4">
                  <div className="catalog-div">
                    <Header as="h3" color="orange">
                      <Icon name="boxes" size="mini" color="brown" />
                      <Header.Content>BUNDLES </Header.Content>
                    </Header>
                    <div style={{ textAlign: 'center' }}>
                      {['MF', VENDORS.NU, VENDORS.CJ].includes(props.vendor) && props.patientId && (
                        <Button
                          fluid
                          primary
                          onClick={handleCreateMultiOrder}
                          //  disabled={selectedItemIds.size === 0}
                        >
                          Create Multi Order
                        </Button>
                      )}
                    </div>
                    {/*
                      The `!` here matches the sort in `onSuccess` above and records
                      the same assumption; see the note there and the defect note on
                      `FoodOrderBundle.effectivePeriod`.
                    */}
                    {bundlesData
                      .sort((a, b) => {
                        const bDate: string = new Date(b.effectivePeriod!.end!).toDateString();
                        const aDate: string = new Date(a.effectivePeriod!.end!).toDateString();
                        return Date.parse(bDate) - Date.parse(aDate);
                      })
                      .map((bundle, index) => {
                        /*
                         * `selectedBundleId` keeps upstream's `selectedBundle && selectedBundle.bundleId`
                         * rather than `selectedBundle?.bundleId`: `selectedBundle` starts as `null`, so
                         * the `&&` form yields `null` while nothing is selected where `?.` yields
                         * `undefined`. FoodBundleCard's only use is
                         * `props.selectedBundleId === bundle.bundleId`, and `bundleId` is optional —
                         * a server bundle without one would compare equal to `undefined` and render
                         * itself highlighted as selected.
                         */
                        return (
                          <FoodBundleCard
                            key={index.toString() + props.patientId}
                            patientId={props.patientId}
                            selectedBundleId={selectedBundle && selectedBundle.bundleId}
                            bundleName={bundleName}
                            bundle={bundle}
                            onBundleSelect={handleBundleSelect}
                            programMAXMealEquivalents={props.programMAXMealEquivalents}
                            checkedBundle={checkedBundle}
                            setCheckedBundle={setCheckedBundle}
                          />
                        );
                      })}
                  </div>
                </Grid.Column>
                <Grid.Column width="7">
                  <div className="catalog-div">
                    {selectedBundle && (
                      <>
                        <Header as="h3" color="orange">
                          <Icon name="box" size="mini" color="violet" />
                          <Header.Content>{bundleName(selectedBundle.bundleId)}</Header.Content>
                        </Header>
                        <FoodBundleItems
                          ordersMealEquivalents={props.ordersMealEquivalents}
                          setOrdersMealEquivalents={props.setOrdersMealEquivalents}
                          programMAXMealEquivalents={props.programMAXMealEquivalents}
                          setProgramOrdersCost={props.setProgramOrdersCost}
                          programOrdersCost={props.programOrdersCost}
                          programMAXBudget={props.programMAXBudget}
                          setProgramMAXBudget={props.setProgramMAXBudget}
                          patient={props.patient}
                          bundle={selectedBundle}
                          handleMenuItemClick={props.handleMenuItemClick}
                          onItemClick={handleItemClick}
                          clickedItem={clickedItem}
                          profileData={props.profileData}
                          patientId={props.patientId}
                          org={props.org}
                          vendor={props.vendor}
                          ref={foodBundleItemsRef as React.Ref<React.ElementRef<typeof FoodBundleItems>>}
                          setCheckedBundle={setCheckedBundle}
                          checkedBundle={checkedBundle}
                          allBundles={allBundles}
                          allReferralData={props.allReferralData}
                          budgetOverrideChecked={props.budgetOverrideChecked}
                          setBudgetOverrideChecked={props.setBudgetOverrideChecked}
                          ordersMutate={props.ordersMutate}
                          programBudget={props.programBudget}
                          setProgramBudget={props.setProgramBudget}
                        />
                      </>
                    )}
                  </div>
                </Grid.Column>
                <Grid.Column width="5">
                  <div className="catalog-div">
                    {clickedItem && <FoodItemDetail item={clickedItem} />}
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          )}

          {bundlesError && (
            <Message negative>
              <Message.Header>Oops!. Something went wrong.. </Message.Header>
              <p>{bundlesError.message}</p>
            </Message>
          )}
        </div>
      )}
    </>
  );
}

export default Catalog;
